import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';

import { formatEuroFromCents } from '../../operatorTickets/utils';

import { formatFirstRenewalDate } from './date';

/** The two billing modes a "both" ticket (Jahresticket) can be bought under. */
export type TBillingChoice = 'recurring_charge_per_period' | 'single_charge';

/** Figma radio: neutral ring when off, primary ring + filled dot when on. */
const RadioIndicator: FC<{ checked: boolean }> = ({ checked }) => (
	<Box
		sx={{
			flexShrink: 0,
			width: 24,
			height: 24,
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			border: (theme) =>
				`1.5px solid ${
					checked
						? theme.palette.primary.main
						: theme.palette.neutral?.[200] ?? '#b2b2b2'
				}`,
		}}
	>
		{checked && (
			<Box
				sx={{
					width: 12,
					height: 12,
					borderRadius: '50%',
					backgroundColor: 'primary.main',
				}}
			/>
		)}
	</Box>
);

interface IYearlyPaymentOptionsProps {
	operatorName?: string;
	ticketName: string;
	/** The per-period (monthly) rate in cents — the resolved zone/group price. */
	perPeriodPriceInCents: number;
	/** Number of billing periods (12 for a Jahresticket). */
	periodCount: number;
	/** ISO start date, for the monthly first-renewal line. */
	startDate: string;
	value: TBillingChoice;
	onChange: (value: TBillingChoice) => void;
}

interface IOptionCardProps {
	selected: boolean;
	title: string;
	priceLine: string;
	price: string;
	testId: string;
	onClick: () => void;
	children?: ReactNode;
}

const OptionCard: FC<IOptionCardProps> = ({
	selected,
	title,
	priceLine,
	price,
	testId,
	onClick,
	children,
}) => {
	const { t } = useTranslation('ticketshop');

	return (
		<Box
			onClick={onClick}
			data-testid={testId}
			sx={{
				cursor: 'pointer',
				backgroundColor: 'neutral.main',
				borderRadius: '8px',
				p: 1.5,
				width: '100%',
				boxSizing: 'border-box',
				borderStyle: 'solid',
				borderWidth: selected ? '0.75px' : '0.5px',
				borderColor: selected ? 'primary.main' : 'solutions.600',
				boxShadow: selected ? '0px 4px 3px rgba(24, 39, 75, 0.12)' : 'none',
			}}
		>
			<Stack direction="row" gap={1} alignItems="center">
				<Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						variant="b2"
						color="primary.main"
						sx={{ fontWeight: 700 }}
					>
						{title}
					</Typography>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						gap={1}
					>
						<Typography variant="b2" color="primary.main">
							{priceLine}
						</Typography>
						<Typography variant="b2" color="primary.main" noWrap>
							{price}
						</Typography>
					</Stack>
					<Typography variant="b2" color="primary.main">
						{t('payment.vat_note')}
					</Typography>
					{children}
				</Stack>
				<RadioIndicator checked={selected} />
			</Stack>
		</Box>
	);
};

/**
 * Monthly-vs-one-time billing choice for a "both" ticket (Wendland
 * Jahresticket, MC-11411). Monthly bills the per-period rate on the 1st of each
 * month; one-time charges period × count once. Monthly is preselected.
 */
export const YearlyPaymentOptions: FC<IYearlyPaymentOptionsProps> = ({
	operatorName,
	ticketName,
	perPeriodPriceInCents,
	periodCount,
	startDate,
	value,
	onChange,
}) => {
	const { t } = useTranslation('ticketshop');
	const monthlyPrice = formatEuroFromCents(perPeriodPriceInCents);
	const oneTimePrice = formatEuroFromCents(perPeriodPriceInCents * periodCount);
	const priceLine = t('payment.price_line', {
		operator: operatorName ?? '',
		ticket: ticketName,
	});

	return (
		<Stack gap={1} sx={{ width: '100%' }}>
			<OptionCard
				selected={value === 'recurring_charge_per_period'}
				title={t('payment.option_monthly_title')}
				priceLine={priceLine}
				price={monthlyPrice}
				testId="ticket-detail-payment-monthly"
				onClick={() => onChange('recurring_charge_per_period')}
			>
				<Typography variant="l1" color="solutions.600">
					{t('payment.subscription_recurring', {
						price: monthlyPrice,
						count: periodCount,
					})}
					{'. '}
					{t('payment.subscription_first_renewal', {
						date: formatFirstRenewalDate(startDate),
					})}
				</Typography>
			</OptionCard>

			<OptionCard
				selected={value === 'single_charge'}
				title={t('payment.option_onetime_title')}
				priceLine={priceLine}
				price={oneTimePrice}
				testId="ticket-detail-payment-onetime"
				onClick={() => onChange('single_charge')}
			/>
		</Stack>
	);
};
